<template>
    <div>
        <banner
            type="error"
            class="q-mb-md"
            :hasAction="true"
            @action="loadAssets"
            :actionLabel="$t('Reload assets')"
            :isVisible="checkAssetsLoadingFailed(facilityId)"
            :message="
                $t(
                    'Failed to load assets. Press the reload button to try again.'
                )
            "
        />
        <banner
            type="error"
            class="q-mb-md"
            :hasAction="true"
            @action="refreshPage"
            :actionLabel="$t('Refresh')"
            :isVisible="showRefreshBanner"
            :message="
                $t(
                    'Failed to load devices. Press the refresh button to try again.'
                )
            "
        />
        <div v-if="assets.length > 0 && !checkAssetsLoadingFailed(facilityId)">
            <div :class="stickyDivClasses">
                <!-- Button row  -->
                <div class="row q-mb-md">
                    <div class="col-sm-6 col-xs-12">
                        <div
                            class="row"
                            :class="
                                $q.screen.lt.sm
                                    ? 'justify-center'
                                    : 'justify-start'
                            "
                        >
                            <q-btn-group outline>
                                <q-btn
                                    @click="updateFilter('showAll')"
                                    :label="$t('Show All')"
                                    :outline="activeFilter !== 'showAll'"
                                    :color="
                                        activeFilter === 'showAll'
                                            ? 'accent'
                                            : 'white'
                                    "
                                    :text-color="
                                        activeFilter === 'showAll'
                                            ? 'white'
                                            : 'accent'
                                    "
                                ></q-btn>

                                <q-btn
                                    v-for="(button, index) in filterButtonGroup"
                                    :key="index"
                                    :outline="activeFilter !== button.status"
                                    @click="updateFilter(button.status)"
                                    :icon="
                                        ASSET_STATUS_TO_STYLE[button.status][
                                            'icon'
                                        ]
                                    "
                                    :color="
                                        activeFilter === button.status
                                            ? 'accent'
                                            : 'white'
                                    "
                                    :text-color="
                                        activeFilter === button.status
                                            ? 'white'
                                            : 'accent'
                                    "
                                >
                                    <q-tooltip>{{ button.label }}</q-tooltip>
                                </q-btn>
                            </q-btn-group>
                        </div>
                    </div>

                    <div class="col-sm-6 col-xs-12 buttons">
                        <div
                            class="row"
                            :class="
                                $q.screen.lt.sm
                                    ? 'justify-center q-mt-md'
                                    : 'justify-end'
                            "
                        >
                            <q-btn
                                id="report-button"
                                v-if="$userCan('view', 'reports')"
                                @click="
                                    $router.push({
                                        name: 'reports_select',
                                        query: {
                                            facility: facilityId
                                        }
                                    })
                                "
                                :label="$q.screen.gt.sm ? $t('Reports') : ''"
                                class="q-mr-sm bg-white"
                                icon="fas fa-chart-area"
                                text-color="accent"
                                outline
                            />
                            <q-btn-dropdown
                                v-if="isTrackingQualityEvents"
                                id="events-button"
                                class="q-mr-sm bg-white"
                                text-color="accent"
                                :label="$q.screen.gt.sm ? $t('Events') : ''"
                                icon="fa-regular fa-calendar-exclamation"
                                outline
                            >
                                <q-list>
                                    <q-item
                                        id="downtime-events"
                                        clickable
                                        v-close-popup
                                        @click="
                                            $router.push({
                                                name: 'event_list'
                                            })
                                        "
                                    >
                                        <q-item-section>
                                            <q-item-label class="text-accent">
                                                <q-icon
                                                    size="sm"
                                                    class="q-mr-xs"
                                                    name="fas fa-exclamation-circle"
                                                />
                                                {{ $t('Downtime Events') }}
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>

                                    <q-item
                                        id="quality-events"
                                        clickable
                                        v-close-popup
                                        @click="
                                            $router.push({
                                                name: 'quality_event_list'
                                            })
                                        "
                                    >
                                        <q-item-section class="text-accent">
                                            <q-item-label>
                                                <q-icon
                                                    size="sm"
                                                    class="q-mr-xs"
                                                    name="fa-solid fa-clipboard-list-check"
                                                />
                                                {{ $t('Quality Events') }}
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                            </q-btn-dropdown>
                            <q-btn
                                v-else
                                id="events-button"
                                @click="
                                    $router.push({
                                        name: 'event_list'
                                    })
                                "
                                :label="$q.screen.gt.sm ? $t('Events') : ''"
                                icon="fas fa-exclamation-circle"
                                class="q-mr-sm bg-white"
                                text-color="accent"
                                outline
                            />
                            <q-btn
                                id="facility-settings-btn"
                                @click="
                                    $router.push({
                                        name: 'facility_settings',
                                        params: {
                                            facilityId: facilityId
                                        }
                                    })
                                "
                                :label="$q.screen.gt.sm ? $t('Settings') : ''"
                                class="bg-white"
                                text-color="accent"
                                outline
                                icon="fas fa-cogs"
                            />
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-lg-2 col-md-3 col-sm-4 col-xs-12">
                        <q-select
                            ref="qselectAssetGroup"
                            outlined
                            dense
                            v-model="selectedGroups"
                            use-input
                            use-chips
                            multiple
                            input-debounce="0"
                            @input="clearFilterAssetGroup"
                            :options="selectedGroupOptions"
                            option-value="id"
                            option-label="name"
                            @filter="filterGroupSelection"
                            :label="$t('Select groups')"
                            behavior="menu"
                            @add="addSelectedGroupPerFacility"
                            @remove="removeSelectedGroupPerFacility"
                            class="bg-white"
                            color="accent"
                        >
                            <template v-slot:selected-item="scope">
                                <q-chip
                                    removable
                                    dense
                                    @remove="scope.removeAtIndex(scope.index)"
                                    clickable
                                    @click="scope.removeAtIndex(scope.index)"
                                    :tabindex="scope.tabindex"
                                    color="accent"
                                    text-color="white"
                                    class="q-mx-xs"
                                    :style="{
                                        backgroundColor: '#027be3',
                                        fontSize: '15px'
                                    }"
                                >
                                    {{ scope.opt.name }}
                                </q-chip>
                            </template>
                            <template v-slot:no-option>
                                <q-item
                                    clickable
                                    @click="
                                        $router.push({
                                            name: 'group_create',
                                            params: {
                                                facilityId: facilityId
                                            }
                                        })
                                    "
                                >
                                    <q-item-section class="text-italic" avatar>
                                        <q-icon
                                            color="primary"
                                            name="fas fa-plus"
                                        />
                                    </q-item-section>
                                    <q-item-section>
                                        {{ $t('Add New Group') }}
                                    </q-item-section>
                                </q-item>
                            </template>
                            <template v-slot:after-options>
                                <q-separator />
                                <q-item
                                    clickable
                                    @click="
                                        $router.push({
                                            name: 'group_create',
                                            params: {
                                                facilityId: facilityId
                                            }
                                        })
                                    "
                                >
                                    <q-item-section class="text-italic" avatar>
                                        <q-icon
                                            color="primary"
                                            name="fas fa-plus"
                                        />
                                    </q-item-section>
                                    <q-item-section>
                                        {{ $t('Add New Group') }}
                                    </q-item-section>
                                </q-item>
                            </template>
                        </q-select>
                    </div>

                    <div
                        class="col-lg-2 col-md-3 col-sm-4 col-xs-12"
                        :class="$q.screen.lt.sm ? 'q-mt-md' : 'q-ml-md'"
                    >
                        <q-select
                            ref="qSelectPeriod"
                            outlined
                            dense
                            v-model="selectedPeriod"
                            :options="periods"
                            :label="$t('Show stats for')"
                            class="bg-white"
                            color="accent"
                        ></q-select>
                    </div>

                    <div
                        class="col-lg-2 col-md-3 col-sm-3 col-xs-12"
                        :class="$q.screen.lt.sm ? 'q-mt-md' : 'q-ml-md'"
                        style="margin-left: auto"
                    >
                        <q-input
                            v-model="search"
                            @clear="search = ''"
                            :placeholder="$t('Search...')"
                            debounce="10"
                            clearable
                            outlined
                            dense
                            style="width: 100%"
                            class="bg-white"
                            color="accent"
                        >
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                        </q-input>
                    </div>
                </div>
            </div>

            <!-- Cards section -->
            <div class="row q-col-gutter-lg">
                <div
                    @click="selectAsset(asset.id)"
                    class="col-lg-3 col-md-4 col-sm-6 col-xs-12"
                    v-for="(asset, index) in filteredAssets"
                    :key="index"
                >
                    <asset-card
                        :data-test="`asset-card-${asset.id}`"
                        :assetId="asset.id"
                        :assetData="this.assetData[asset.uuid]"
                        :statsPeriod="selectedPeriod.value"
                        :isLoadingStats="!isAssetLoaded(asset.id)"
                        :isLoadingDeviceStatus="isLoadingDeviceStatus"
                    />
                </div>
            </div>
        </div>

        <!-- No assets loaded -->
        <div
            v-if="assets.length === 0 && !checkAssetsLoadingFailed(facilityId)"
        >
            <!-- User has no access to any facility -->
            <div v-if="isSuperuser" class="row">
                <div class="col">
                    {{
                        $t(
                            'No assets found. This could be due to limited access or assets not yet initialized. Go to the settings and log in to the Admin Portal to set access or create facilities.'
                        )
                    }}
                </div>
                <q-btn
                    @click="
                        $router.push({
                            name: 'settings'
                        })
                    "
                    :label="$t('Settings')"
                    color="secondary"
                    icon="fas fa-cogs"
                ></q-btn>
            </div>
            <!-- Customer account has no facilities -->
            <div v-else>
                {{
                    $t(
                        'No assets found. This could be due to limited access or assets not yet initialized. Contact your account admin.'
                    )
                }}
            </div>
        </div>
    </div>
</template>

<script>
import AssetCard from '@components/AssetCard.vue';
import Banner from '@components/Banner.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import StickyTop from '@mixins/StickyTop.vue';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import ConnectionManager from '@src/connectionManager.js';
import { mapGetters } from 'vuex';
import { useSettingsStore } from '@stores/settings';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities.js';

export default {
    name: 'FacilityDashboard',
    components: { AssetCard, Banner },
    mixins: [BreadcrumbsManager, StickyTop],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        facilityId: {
            immediate: true,
            async handler() {
                // update breadcrumb
                this.setBreadcrumbs([
                    {
                        label: this.$t('Facilities'),
                        route: {
                            name: 'facility_list'
                        }
                    },
                    {
                        label: this.facility ? this.facility?.name : ''
                    }
                ]);
                // whenever a change in facility id is detected,
                // get the stored asset groups filters
                // from the state for that particular facility.
                this.initAssetGroupSelection();
            }
        }
    },
    data() {
        return {
            // mock data - AssetGroup
            selectedGroupOptions: [],
            selectedGroups: [],
            search: '',
            // model
            ASSET_STATUS_TO_STYLE,
            // asset data
            // ex. { "1": { key: value }, "2": { key: value } }
            assetData: {},
            // stats period selection
            selectedPeriod: {
                label: this.$t('Current product'),
                value: 'currentProduct'
            },
            periods: [
                {
                    label: this.$t('Current product'),
                    value: 'currentProduct'
                },
                {
                    label: this.$t('Current shift'),
                    value: 'currentShift'
                },
                {
                    label: this.$t('Today'),
                    value: 'today'
                }
            ],

            // filter button group
            activeFilter: 'showAll',
            filterButtonGroup: [
                {
                    status: 'running',
                    label: this.$t('Running')
                },
                {
                    status: 'stopped',
                    label: this.$t('Shutdown')
                },
                {
                    status: 'changeover',
                    label: this.$t('Changeover')
                },
                {
                    status: 'in_downtime',
                    label: this.$t('In Downtime')
                }
            ],
            disconnectionStatusPollingId: null,
            loadedAssets: new Set(),
            showRefreshBanner: false,

            isLoadingDeviceStatus: false
        };
    },
    computed: {
        ...mapGetters({
            getFacility: 'facilities/getFacility',
            getAssetStatus: 'facilities/getAssetStatus',
            getAssetProduct: 'facilities/getAssetProduct',
            getAssetGroups: 'facilities/getAssetGroups',
            checkAssetsLoadingFailed: 'facilities/checkAssetsLoadingFailed'
        }),
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useAuthStore, ['isSuperuser']),
        ...mapState(useFacilitiesStore, ['getShiftsByFacility']),
        /**
         * Get assets for this facility.
         * @return {Object[]}
         */
        assets() {
            return this.$store.getters['facilities/getAssets'](this.facilityId);
        },
        /**
         * Get asset groups for this facility.
         * @return {Object[]}
         */
        assetGroups() {
            return this.$store.getters['facilities/getAssetGroups'](
                this.facilityId
            );
        },
        /**
         * Return the current facility.
         * @return {Object[]}
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Return assets with matching active status.
         * @return {Object[]}
         */
        filteredAssets() {
            return this.assets.filter(asset => {
                // check asset status
                if (this.activeFilter !== 'showAll') {
                    if (this.activeFilter !== this.getAssetStatus(asset.id)) {
                        return false;
                    }
                }
                // check group selection
                if (this.selectedGroups.length !== 0) {
                    // check if any of the group the asset is part of is currently selected
                    let found = false;
                    this.selectedGroups.forEach(selectedGroup => {
                        if (asset.groups) {
                            asset.groups.forEach(assetGroup => {
                                if (assetGroup.id === selectedGroup.id) {
                                    found = true;
                                }
                            });
                        }
                    });
                    if (!found) {
                        return false;
                    }
                }
                // check search field
                if (this.search !== '') {
                    if (
                        !asset.name
                            .toLowerCase()
                            .includes(this.search.toLowerCase())
                    ) {
                        return false;
                    }
                }

                return true;
            });
        }
    },
    methods: {
        ...mapActions(useAuthStore, ['isAuthenticatedAction']),
        ...mapActions(useFacilitiesStore, ['loadShiftsAction']),
        /**
         * Callback to execute when there is a new stream event.
         * @param {Object} event - Stream event data.
         */
        handleNewFacilityEvent(event) {
            console.debug('New facility event data received');

            // check if event is for this facility
            if (this.facility.uuid !== event['facilityUUID']) {
                // do nothing, don't process this message
                return;
            }

            for (const asset_event of event['assets']) {
                // find the asset that corresponds to the event
                const asset = this.assets.find(
                    asset => asset.uuid === asset_event['assetUUID']
                );
                // if the user does not hav access to the asset,
                // dont process the event
                if (!asset) {
                    continue;
                }
                // check that the product id of the event matches the currently running product for the asset
                if (
                    this.getAssetProduct(asset.id) !== asset_event['productID']
                ) {
                    // do nothing, don't process this message
                    continue;
                }
                // check if asset data already exists in assetData
                if (this.assetData[asset_event['assetUUID']]) {
                    // only update the asset data if the new event has a newer timestamp
                    if (
                        this.assetData[asset_event['assetUUID']]['timestamp'] <
                        asset_event['timestamp']
                    ) {
                        this.assetData[asset_event['assetUUID']] = asset_event;
                    }
                } else {
                    this.assetData[asset_event['assetUUID']] = asset_event;
                }
                // add the asset id to the loadedAssets set
                this.loadedAssets.add(asset.id);
            }
        },
        /**
         * Handle select asset
         * @param {Number} assetId
         */
        selectAsset(assetId) {
            // update selected asset in store
            this.$store.dispatch('facilities/selectAsset', assetId);

            // navigate user
            this.$router.push({
                name: 'asset_dashboard',
                params: { assetId: assetId }
            });
        },
        /**
         * Change the active filter.
         */
        updateFilter(filter) {
            this.activeFilter = filter;
        },
        /**
         * Load assets.
         */
        async loadAssets() {
            await this.$store.dispatch('facilities/loadAssets', {
                facilityId: this.facilityId
            });
        },
        /**
         * Load shifts for the facility.
         * Only fetches from API if shifts haven't been loaded yet.
         */
        async loadFacilityShifts() {
            // Check if shifts already exist in store
            const existingShifts = this.getShiftsByFacility(this.facilityId);

            // Only load if shifts haven't been fetched yet (null)
            if (existingShifts === null) {
                try {
                    await this.loadShiftsAction(this.facilityId);
                    console.debug(
                        `Shifts loaded for facility ${this.facilityId}`
                    );
                } catch (error) {
                    console.error(
                        `Failed to load shifts for facility ${this.facilityId}`,
                        error
                    );
                    // Don't show user error - shifts loading is non-critical for dashboard
                }
            }
        },
        /**
         * Allow the user to filter the list of available groups
         */
        filterGroupSelection(val, update) {
            update(() => {
                if (val === '') {
                    this.selectedGroupOptions = this.assetGroups;
                } else {
                    const needle = val.toLowerCase();
                    this.selectedGroupOptions = this.assetGroups.filter(
                        v => v.name.toLowerCase().indexOf(needle) > -1
                    );
                }
            });
        },
        /**
         *  Reset the group selection field after the user select a group
         */
        clearFilterAssetGroup() {
            if (this.$refs.qselectAssetGroup !== void 0) {
                this.$refs.qselectAssetGroup.updateInputValue('');
            }
        },
        /**
         * Add selected group in filter to the state
         * @param(Object): selectedAssetGroup
         */
        addSelectedGroupPerFacility(selectedAssetGroup) {
            this.$store.dispatch('facilities/addSelectedGroupPerFacility', {
                facilityId: this.facilityId,
                selectedAssetGroup: selectedAssetGroup.value
            });
        },
        /**
         * Remove the asset group the store
         * @param(Object): removedAssetGroup
         */
        removeSelectedGroupPerFacility(removedAssetGroup) {
            this.$store.dispatch('facilities/removeSelectedGroupPerFacility', {
                facilityId: this.facilityId,
                removedAssetGroup: removedAssetGroup.value
            });
        },
        initAssetGroupSelection() {
            // get stored asset group filters from the state
            // and assign to the selectedGroups data property.
            const storedAssetGroupsPerFacility = this.$store.getters[
                'facilities/getStoredAssetGroupsPerFacility'
            ](this.facilityId);
            this.selectedGroups = storedAssetGroupsPerFacility;
        },
        async loadDisconnectionStatuses() {
            // get the gateway disconnection status
            // for this facility
            try {
                await this.$store.dispatch(
                    'facilities/loadDisconnectionStatuses',
                    {
                        facilityId: this.facilityId
                    }
                );
            } catch (error) {
                console.error(
                    'Error while loading the gateway disconnection statuses'
                );
            }
        },
        /**
         * Check if the asset is in the loadedAssets set
         * @param {Number} assetId
         * @return {Boolean}
         */
        isAssetLoaded(assetId) {
            return this.loadedAssets.has(assetId);
        },
        /**
         * Refresh the page
         */
        refreshPage() {
            window.location.reload();
        }
    },
    async created() {
        // whenever the component gets created,
        // get the stored asset groups filters
        // from the state for particular facility.
        this.initAssetGroupSelection();

        // Check the disconnection statuses immediately
        this.loadDisconnectionStatuses();

        // Poll every 5 seconds to refresh the list of disconnection statuses
        this.disconnectionStatusPollingId = setInterval(() => {
            this.loadDisconnectionStatuses();
        }, 5000);

        // wait until signalr is connected before registering handler
        this.poll = setInterval(() => {
            console.debug('Checking if SignalR is connected...');
            if (ConnectionManager.isConnected()) {
                // register handler
                ConnectionManager.addHandler({
                    name: 'newFacilityEvent',
                    handler: this.handleNewFacilityEvent
                });
                clearInterval(this.poll);
            }
        }, 100);

        try {
            this.isLoadingDeviceStatus = true;
            // Load all assets connected to a device in the facility
            await this.$store.dispatch(
                'facilities/loadFacilityDevices',
                this.facilityId
            );
        } catch (error) {
            console.error('Error while loading facility devices');
            this.showRefreshBanner = true;
        } finally {
            this.isLoadingDeviceStatus = false;
        }

        // Load shifts for this facility if not already loaded
        this.loadFacilityShifts();
    },
    async beforeUnmount() {
        // before unmounting this component
        // clear the poll for the gateway
        // disconnection status
        if (this.disconnectionStatusPollingId) {
            clearInterval(this.disconnectionStatusPollingId);
        }

        if (ConnectionManager.isInitialized()) {
            // remove handler
            await ConnectionManager.removeHandler('newFacilityEvent');
            // Check if user is logged in before sending a request.
            // This check is important in the event that the user logs out from the facility dashboard page
            if (await this.isAuthenticatedAction()) {
                // unsubscribe from this asset
                await ConnectionManager.unsubscribeFromFacility(
                    this.facilityId
                );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.button-group {
    border: 1px solid $secondary;
}
</style>
